import React, { useEffect, useState, useRef } from "react";
import { kelvinToRGB } from "../common/color-util";
const ColorTempNumber1Widget = ({
  widget,
  onValueUpdate,
  isEditing = false,
}) => {
  const [colorTemp, setColorTemp] = useState(2700);
  const [widgetData, setWidgetData] = useState(null);
  const [tempMinMax, setTempMinMax] = useState({ min: 2700, max: 6500 });
  const dragging = useRef(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const sliderRef = React.useRef(null);
  const latestColorTempRef = useRef(2700);
  const touchStartRef = useRef(null);
  const isScrolling = useRef(false);

  useEffect(() => {
    if (!widget) return;
    setWidgetData(widget);

    if (widget.value) {
      setColorTemp(widget.value || 2700);
    }
  }, [widget]);

  const handleValueUpdate = (data) => {
    if (isEditing || !widgetData) return;

    const updatedWidget = {
      ...widgetData,
      value: data,
    };

    onValueUpdate(updatedWidget);
  };

  const gradientStops = [];
  const steps = 10;

  for (let i = 0; i <= steps; i++) {
    const temp =
      tempMinMax.min + (i / steps) * (tempMinMax.max - tempMinMax.min);
    const rgb = kelvinToRGB(temp);
    gradientStops.push(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
  }

  const gradientStyle = {
    background: `linear-gradient(to right, ${gradientStops.join(", ")})`,
  };

  const calculateTemperature = (clientX) => {
    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = (clientX - rect.left) / rect.width;
    const clampedPercentage = Math.max(0, Math.min(1, percentage));
    return Math.round(
      tempMinMax.min + clampedPercentage * (tempMinMax.max - tempMinMax.min)
    );
  };

  const handleInteraction = (clientX) => {
    if (isEditing) return;
    const newTemp = calculateTemperature(clientX);
    if (newTemp !== colorTemp) {
      setColorTemp(newTemp);
      latestColorTempRef.current = newTemp;
    }
  };

  const handleMouseMove = (e) => {
    if (!dragging.current) return;
    e.preventDefault();
    requestAnimationFrame(() => {
      handleInteraction(e.clientX);
    });
  };

  const handleMouseUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setShowTooltip(false);
    handleValueUpdate(latestColorTempRef.current);
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleMouseDown = (e) => {
    if (isEditing) return;
    e.preventDefault();
    dragging.current = true;
    setShowTooltip(true);
    handleInteraction(e.clientX);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleTouchMove = (e) => {
    if (isScrolling.current || !touchStartRef.current) return;

    if (dragging.current) {
      e.preventDefault();
      requestAnimationFrame(() => {
        handleInteraction(e.touches[0].clientX);
      });
      return;
    }

    const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
    const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
    const moveThreshold = 5;

    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      if (deltaY > deltaX) {
        isScrolling.current = true;
        touchStartRef.current = null;
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
      } else {
        e.preventDefault();
        dragging.current = true;
        setShowTooltip(true);
        requestAnimationFrame(() => {
          handleInteraction(e.touches[0].clientX);
        });
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (isScrolling.current) {
      isScrolling.current = false;
      touchStartRef.current = null;
      return;
    }

    if (dragging.current) {
      setShowTooltip(false);
      handleValueUpdate(latestColorTempRef.current);
    } else {
      handleInteraction(e.changedTouches[0].clientX);
      handleValueUpdate(latestColorTempRef.current);
    }

    dragging.current = false;
    isScrolling.current = false;
    touchStartRef.current = null;
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
  };

  const handleTouchStart = (e) => {
    if (isEditing) return;
    dragging.current = false;
    isScrolling.current = false;
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
  };

  const thumbPosition =
    ((colorTemp - tempMinMax.min) / (tempMinMax.max - tempMinMax.min)) * 100;

  return (
    <div
      className="relative w-full h-full flex items-center"
      onClick={(e) => e.stopPropagation()}
    >
      {showTooltip && (
        <div
          className="absolute -top-[25px] -translate-x-1/2 bg-white rounded-lg px-2 py-1 text-black text-sm font-bold z-[1] pointer-events-none shadow-lg"
          style={{
            left: `${thumbPosition}%`,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
          }}
        >
          {colorTemp}K
        </div>
      )}
      <div
        ref={sliderRef}
        className={`relative w-full h-full rounded-2xl select-none outline-none ${
          isEditing ? "cursor-default" : "cursor-pointer"
        }`}
        style={{
          WebkitTapHighlightColor: "transparent",
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          userSelect: "none",
          ...gradientStyle,
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div
          className="absolute bg-white rounded shadow-sm top-1/2 -translate-y-1/2 pointer-events-none"
          style={{
            width: "4px",
            height: "60%",
            left: `${thumbPosition}%`,
            marginLeft: "-2px",
            boxShadow: "0 0 4px rgba(0, 0, 0, 0.3)",
          }}
        />
      </div>
    </div>
  );
};

export default ColorTempNumber1Widget;
